import type { FC, HTMLProps, ReactNode } from "react";

import { Skeleton } from "../Skeleton";

import * as styles from "./Typography.styles";

export type TypographyType = "h1" | "h2" | "h3" | "h4" | "h5" | "body" | "footnote" | "caption";

interface Props extends HTMLProps<HTMLElement> {
  /**
   * Determines the heading size
   *
   * @default body
   */
  type?: TypographyType;
  /**
   * Option for overriding size of heading tags
   * Only applies when using type h1, h2, h3, h4
   */
  sizeOverride?: "h1" | "h2" | "h3" | "h4" | "h5" | "body" | "footnote" | "caption";
  /**
   * Option to indent the text
   *
   * @default false
   */
  isIndented?: boolean;
  /**
   * An option to utilize the secondary weight for that typography type
   *
   * @default false
   */
  isSecondaryWeight?: boolean;
  /**
   * Option to set the block to extend 100% width
   *
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * Option to remove bottom margins from element
   *
   * @default false
   */
  isMarginless?: boolean;
  /**
   * Option to have lighter font color
   *
   * @default false
   */
  isMuted?: boolean;
  /**
   * Displays the corresponding Skeleton
   *
   * @default false
   */
  isSkeleton?: boolean;
  /**
   * Typography content, basically text
   *
   * @default undefined
   */
  children?: ReactNode;
  /**
   * Only present to strip out data-testids for old Enzyme tests
   * Can be removed once all tests are in RTL!
   */
  "data-testid"?: string;
}

const Typography: FC<Props> = ({
  type,
  sizeOverride,
  isIndented,
  isSecondaryWeight,
  isFullWidth,
  isMarginless,
  isMuted,
  isSkeleton,
  children,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ref,
  ...rest
}: Props) => {
  const titleStyleBuilder = () => {
    return [
      styles.titleBase,
      styles.titleSizes[type](isSecondaryWeight),
      isMuted && styles.isMuted,
      isIndented && styles.indented,
      isFullWidth && styles.fullwidth,
      !isMarginless && styles.margin,
      sizeOverride && styles.sizeOverride[sizeOverride](isSecondaryWeight),
    ];
  };

  const paragraphStyleBuilder = () => {
    return [
      styles.paragraphBase,
      styles.paragraphSizes[type](isSecondaryWeight),
      isMuted && styles.isMuted,
      isFullWidth && styles.fullwidth,
      !isMarginless && styles.margin,
      sizeOverride && styles.sizeOverride[sizeOverride](isSecondaryWeight),
    ];
  };

  const renderType = () => {
    switch (type) {
      case "h1":
        return (
          <h1 css={titleStyleBuilder()} {...rest}>
            {children}
          </h1>
        );
      case "h2":
        return (
          <h2 css={titleStyleBuilder()} {...rest}>
            {children}
          </h2>
        );
      case "h3":
        return (
          <h3 css={titleStyleBuilder()} {...rest}>
            {children}
          </h3>
        );
      case "h4":
        return (
          <h4 css={titleStyleBuilder()} {...rest}>
            {children}
          </h4>
        );
      case "h5":
        return (
          <h5 css={titleStyleBuilder()} {...rest}>
            {children}
          </h5>
        );
      default:
        return (
          <p css={paragraphStyleBuilder()} {...rest}>
            {children}
          </p>
        );
    }
  };

  const getSize = () => {
    if (sizeOverride) {
      return sizeOverride;
    }
    return type ? type : "body";
  };

  const getElement = () => {
    if (isSkeleton) {
      return <Skeleton type="typography" typographySize={getSize()} isFullwidth={isFullWidth} />;
    }
    return renderType();
  };

  return getElement();
};

Typography.defaultProps = {
  type: "body",
  isSecondaryWeight: false,
  isMuted: false,
};

export { Typography };
